// SOUND-546 (Phase 3, first half) — conditional step spawning.
//
// A workflow step (`ticketTemplates.childSteps[]`) may carry `spawnWhen`, a
// predicate with EXACTLY the shape intake fields use for `visibleWhen`:
//   { field: <intake field key>, operator: "eq"|"neq"|"in"|"notIn", value }
// and it is evaluated by the SAME evaluator the intake form uses
// (`evaluateVisibleWhen`). This module never re-implements that comparison; it
// only decides WHAT to evaluate it against, which is where the two contracts
// differ:
//
//   1. Values are the ones the SERVER would store — visible, non-empty fields
//      only, coerced as `validateIntakeValues` coerces them (number → Number,
//      "true"/"false" → boolean). So a field hidden by its own `visibleWhen`
//      is unanswered, exactly as it is on the parent ticket.
//   2. Unanswered NEVER matches (SOUND-573). `evaluateVisibleWhen` alone says
//      `neq`/`notIn` are TRUE for an unanswered field (undefined !== x), which
//      is right for visibility and wrong for spawning — "managed != Yes" must
//      not create a step for a question nobody answered. The guard is here,
//      before the shared evaluator runs.
//
// Everything here is pure so it can be pinned in conditionalSteps.test.js —
// sound-app has no component-test harness (SOUND-679).

import { evaluateVisibleWhen, isIntakeValueEmpty } from "constants/ticketTemplates";
import { buildIntakeSubmission, toNumberIfNumeric } from "utilities/intakeSubmission";

const MULTI_OPERATORS = ["in", "notIn"];

export function isConditionalStep(step) {
  return !!(step && step.spawnWhen);
}

/* ── editor model ⇄ API shape ───────────────────────────────────────────── */

// The editor tracks the referenced field by `_id` (`{ fieldId, operator,
// value }`, the VisibleWhenEditor model) so a label edit — which re-derives the
// key — never breaks the reference. These convert at hydrate/serialize, the
// same way WorkflowEditor converts intake `visibleWhen`.

/** API `{field, operator, value}` → editor `{fieldId, operator, value}` (or null). */
export function spawnWhenToEditor(raw, fieldIdByKey = {}) {
  if (!raw || !raw.field || !fieldIdByKey[raw.field]) return null;
  let value = raw.value;
  if (Array.isArray(value)) value = value.join(", ");
  return { fieldId: fieldIdByKey[raw.field], operator: raw.operator || "eq", value: value ?? "" };
}

// The editor's value input is free text for every non-boolean field, but the
// API requires a NUMBER for a number field (and the stored answer is coerced to
// one, so "5" would never equal 5). Convert a numeric string (toNumberIfNumeric);
// leave anything else for the server's `details` to name.

/**
 * Editor `{fieldId, operator, value}` → API `{field, operator, value}` (or null).
 * @param {Object} fieldTypeById - optional `{ [_id]: type }`, for number coercion
 */
export function spawnWhenToApi(ui, fieldKeyById = {}, fieldTypeById = {}) {
  if (!ui || !ui.fieldId || !fieldKeyById[ui.fieldId]) return null;
  const isNumber = fieldTypeById[ui.fieldId] === "number";
  let value = ui.value;
  if (MULTI_OPERATORS.includes(ui.operator)) {
    value = String(value ?? "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    if (isNumber) value = value.map(toNumberIfNumeric);
  } else if (isNumber) {
    value = toNumberIfNumeric(value);
  }
  return { field: fieldKeyById[ui.fieldId], operator: ui.operator, value };
}

/**
 * SOUND-791 — an intake field's `visibleWhen` has spawnWhen's exact shape and
 * the same number rule (the create modal sends a number field's answer as a
 * number; see utilities/intakeSubmission). One conversion, two names.
 */
export const visibleWhenToApi = spawnWhenToApi;
export const visibleWhenToEditor = spawnWhenToEditor;

/**
 * After intake fields are deleted, drop every step condition that pointed at a
 * field that no longer exists. A step may reference ANY intake field (there is
 * no earlier-only rule across the intake/step boundary), so reordering fields
 * never invalidates one — only deletion does.
 * @returns {{ steps: Array, prunedCount: number }}
 */
export function pruneSpawnWhen(steps = [], fieldIds = []) {
  const live = new Set(fieldIds);
  let prunedCount = 0;
  const next = steps.map((s) => {
    if (s.spawnWhen && !live.has(s.spawnWhen.fieldId)) {
      prunedCount += 1;
      return { ...s, spawnWhen: null };
    }
    return s;
  });
  return { steps: next, prunedCount };
}

/* ── evaluation ─────────────────────────────────────────────────────────── */

/**
 * The intake values the server stores on the parent ticket, and therefore the
 * values `spawnWhen` is evaluated against. Mirrors `validateIntakeValues`:
 * the payload the modal sends (buildIntakeSubmission — hidden fields and empty
 * answers absent), with numbers and boolean strings coerced.
 */
export function submittedIntakeValues(intakeFields = [], values = {}) {
  const { fields: sent } = buildIntakeSubmission(intakeFields, values);
  const out = {};
  for (const f of intakeFields || []) {
    if (!f || !f.key) continue;
    if (!(f.key in sent)) continue;
    const v = sent[f.key];
    if (f.type === "number") {
      const n = typeof v === "number" ? v : Number(v);
      out[f.key] = Number.isNaN(n) ? v : n;
    } else if (f.type === "boolean") {
      out[f.key] = v === "true" ? true : v === "false" ? false : v;
    } else {
      out[f.key] = v;
    }
    if (f.type === "select-other" && f.otherKey && f.otherKey in sent) out[f.otherKey] = sent[f.otherKey];
  }
  return out;
}

/**
 * Does this step spawn, given the SUBMITTED values (see submittedIntakeValues)?
 * No condition ⇒ always. Unanswered referenced field ⇒ never, whatever the
 * operator. Otherwise the shared `evaluateVisibleWhen` decides.
 */
export function stepWillSpawn(step, submitted = {}) {
  const pred = step && step.spawnWhen;
  if (!pred) return true;
  if (!pred.field) return false; // a malformed condition must not quietly spawn
  if (isIntakeValueEmpty(submitted[pred.field])) return false;
  return evaluateVisibleWhen(pred, submitted);
}

/**
 * The create-modal plan: which of a template's steps WILL be created and which
 * will be skipped for these intake answers, plus the dependencies the server
 * drops because they point at a skipped step (a dependant is never blocked
 * forever on a step that does not exist).
 *
 * @param {Array} childSteps - API-shape steps ({ key, title, spawnWhen, dependsOnStepKeys })
 * @param {Array} intakeFields - API-shape intake fields
 * @param {Object} values - the RAW intake answers from the form
 * @returns {{ created: Array, skipped: Array, droppedDeps: Object<string, string[]> }}
 *   `created` steps carry `dependsOnStepKeys` with skipped targets removed.
 */
export function planStepSpawns(childSteps = [], intakeFields = [], values = {}) {
  const submitted = submittedIntakeValues(intakeFields, values);
  const skippedKeys = new Set();
  const skipped = [];
  const kept = [];
  for (const s of childSteps) {
    if (!s) continue;
    if (stepWillSpawn(s, submitted)) kept.push(s);
    else {
      skipped.push(s);
      if (s.key) skippedKeys.add(s.key);
    }
  }
  const droppedDeps = {};
  const created = kept.map((s) => {
    const deps = s.dependsOnStepKeys || [];
    const lost = deps.filter((k) => skippedKeys.has(k));
    if (lost.length === 0) return s;
    droppedDeps[s.key] = lost;
    return { ...s, dependsOnStepKeys: deps.filter((k) => !skippedKeys.has(k)) };
  });
  return { created, skipped, droppedDeps };
}

/* ── authoring checks ───────────────────────────────────────────────────── */

/**
 * Non-blocking: a step that ALWAYS spawns but depends on a conditional one.
 * When the condition is not met, the dependency is dropped at instantiation,
 * so the step runs immediately instead of after it — worth knowing, not wrong.
 * Mirrors the API's save-time `warnings`.
 * @param {Array} childSteps - API-shape steps
 * @returns {string[]}
 */
export function conditionalDependencyWarnings(childSteps = []) {
  const byKey = {};
  childSteps.forEach((s) => s && s.key && (byKey[s.key] = s));
  const out = [];
  for (const s of childSteps) {
    if (!s || isConditionalStep(s)) continue;
    for (const k of s.dependsOnStepKeys || []) {
      const dep = byKey[k];
      if (dep && isConditionalStep(dep)) {
        const name = s.title || s.key || "A step";
        const depName = dep.title || dep.key;
        out.push(
          `"${name}" always runs but depends on conditional step "${depName}". When "${depName}" is skipped, that dependency is dropped and "${name}" starts immediately.`
        );
      }
    }
  }
  return out;
}

/**
 * Blocking, client-side: conditions that can never match because no value was
 * picked. (Everything else is the server's call — its `details` list the rest.)
 * @param {Array} childSteps - API-shape steps
 * @returns {string[]}
 */
export function spawnWhenIssues(childSteps = []) {
  const out = [];
  for (const s of childSteps) {
    const p = s && s.spawnWhen;
    if (!p) continue;
    const name = s.title || s.key || "A step";
    const empty = MULTI_OPERATORS.includes(p.operator)
      ? !Array.isArray(p.value) || p.value.length === 0
      : p.value === "" || p.value === null || p.value === undefined;
    if (empty) out.push(`Step "${name}": its condition needs a value.`);
  }
  return out;
}

/** Combine client and server warnings, dropping exact duplicates. */
export function mergeWarnings(...lists) {
  const seen = new Set();
  const out = [];
  lists.flat().forEach((w) => {
    const s = typeof w === "string" ? w : w && (w.message || w.error);
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out;
}

/** The API's non-blocking `warnings` from a save/activate response, wherever it sits. */
export function responseWarnings(resData) {
  const w = resData?.warnings ?? resData?.data?.warnings;
  return Array.isArray(w) ? mergeWarnings(w) : [];
}

/* ── display ────────────────────────────────────────────────────────────── */

const OPERATOR_PHRASES = { eq: "is", neq: "is not", in: "is any of", notIn: "is none of" };

function displayValue(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (Array.isArray(v)) return v.join(", ");
  return v === undefined || v === null || v === "" ? "—" : String(v);
}

/**
 * Plain-language condition, e.g. `Managed services client is Yes`.
 * @param {Object} pred - API-shape predicate
 * @param {Array} intakeFields - fields with `key` + `label`
 */
export function describeSpawnWhen(pred, intakeFields = []) {
  if (!pred || !pred.field) return "";
  const f = intakeFields.find((x) => x && x.key === pred.field);
  const label = (f && (f.label || f.key)) || pred.field;
  return `${label} ${OPERATOR_PHRASES[pred.operator] || pred.operator} ${displayValue(pred.value)}`;
}

/**
 * Ticket detail: the parent's `skippedSteps`. `null` when there is nothing to
 * show — the field absent/null (no template ran) or empty (nothing skipped).
 * @returns {null | { count: number, summary: string, titles: string[] }}
 */
export function skippedStepsDisplay(skippedSteps) {
  if (!Array.isArray(skippedSteps) || skippedSteps.length === 0) return null;
  const titles = skippedSteps.map((s) => (s && (s.title || s.stepKey)) || "Untitled step");
  const count = titles.length;
  return { count, summary: `${count} ${count === 1 ? "step" : "steps"} skipped — conditions not met`, titles };
}
